import * as React from "react"

const MOBILE_BREAKPOINT = 768
const TABLET_BREAKPOINT = 1024

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return !!isMobile
}

export function useDeviceType() {
  const [deviceType, setDeviceType] = React.useState<'mobile' | 'tablet' | 'desktop'>('desktop')

  React.useEffect(() => {
    const updateDeviceType = () => {
      const width = window.innerWidth
      if (width < MOBILE_BREAKPOINT) {
        setDeviceType('mobile')
      } else if (width < TABLET_BREAKPOINT) {
        setDeviceType('tablet')
      } else {
        setDeviceType('desktop')
      }
    }

    updateDeviceType()
    window.addEventListener('resize', updateDeviceType)
    return () => window.removeEventListener('resize', updateDeviceType)
  }, [])

  return {
    deviceType,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
    isTouchDevice: deviceType === 'mobile' || deviceType === 'tablet'
  }
}

// Enhanced iOS detection
export function useIOSDetection() {
  const [isIOS, setIsIOS] = React.useState(false)
  const [isSafari, setIsSafari] = React.useState(false)
  const [isIOSChrome, setIsIOSChrome] = React.useState(false)

  React.useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase()
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent)
    
    // Check for Safari (PWA installable)
    const isSafariBrowser = /safari/.test(userAgent) && !/chrome|crios|fxios|edge/.test(userAgent)
    
    // Check for Chrome on iOS (not PWA installable)
    const isChromeIOS = /crios/.test(userAgent)
    
    setIsIOS(isIOSDevice)
    setIsSafari(isSafariBrowser && isIOSDevice)
    setIsIOSChrome(isChromeIOS && isIOSDevice)
  }, [])

  return { isIOS, isSafari, isIOSChrome }
}

export function usePWA() {
  const [isInstallable, setIsInstallable] = React.useState(false)
  const [isInstalled, setIsInstalled] = React.useState(false)
  const [deferredPrompt, setDeferredPrompt] = React.useState<BeforeInstallPromptEvent | null>(null)
  const [showIOSInstallPrompt, setShowIOSInstallPrompt] = React.useState(false)
  const { isIOS, isSafari, isIOSChrome } = useIOSDetection()

  React.useEffect(() => {
    // Check if already installed
    const isInStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isInWebAppiOS = (window.navigator as Navigator & { standalone?: boolean }).standalone
    const isInstalled = isInStandalone || !!isInWebAppiOS
    setIsInstalled(isInstalled)

    // For iOS Safari, show install prompt if not installed and not dismissed
    if (isIOS && isSafari && !isInstalled) {
      const dismissed = sessionStorage.getItem('ios-install-dismissed') === 'true'
      setShowIOSInstallPrompt(!dismissed)
      setIsInstallable(true)
    }

    // For iOS Chrome, also show prompt but with different instructions
    if (isIOS && isIOSChrome && !isInstalled) {
      const dismissed = sessionStorage.getItem('ios-install-dismissed') === 'true'
      setShowIOSInstallPrompt(!dismissed)
      setIsInstallable(true)
    }

    // Listen for install prompt (Android/Desktop)
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setIsInstallable(true)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener)
    }
  }, [isIOS, isSafari, isIOSChrome])

  const installApp = async () => {
    // For iOS, we can't programmatically install, just show instructions
    if (isIOS && isSafari) {
      setShowIOSInstallPrompt(true)
      return
    }

    // For Android/Desktop with beforeinstallprompt
    if (!deferredPrompt) return

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    
    if (outcome === 'accepted') {
      setIsInstalled(true)
    }
    
    setDeferredPrompt(null)
    setIsInstallable(false)
  }

  const dismissIOSPrompt = () => {
    setShowIOSInstallPrompt(false)
    sessionStorage.setItem('ios-install-dismissed', 'true')
  }

  return {
    isInstallable,
    isInstalled,
    installApp,
    showIOSInstallPrompt,
    dismissIOSPrompt,
    isIOS,
    isSafari,
    isIOSChrome
  }
}

// Type definition for BeforeInstallPromptEvent
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}
